import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import * as authApi from '@/api/auth';
import type { UserInfo } from '@/api/auth';

/**
 * Auth state. The actual credential is the PHPSESSID cookie managed by the
 * PHP backend — we never hold a token in JS. This store just caches the
 * current user info so views don't have to refetch it.
 */
export const useAuthStore = defineStore('auth', () => {
  const user = ref<UserInfo | null>(null);
  const loading = ref(false);
  const error = ref<string | null>(null);
  // Permission-name set, lowercased for case-insensitive lookup. Populated
  // after login / checkSession. Empty for guest sessions; the sidebar's
  // NavGroup treats an empty set as "show everything" optimistically so
  // the shell isn't blank while permissions are still loading.
  const permissions = ref<Set<string>>(new Set());
  const permissionsLoaded = ref(false);

  const isAuthenticated = computed(() => user.value !== null);

  async function acceptExistingLoginSession(): Promise<boolean> {
    const info = await authApi.getUserInfo();
    if (info && info.userId) {
      user.value = info;
      loadPermissions();
      return true;
    }
    return false;
  }

  function hasPermission(key: string): boolean {
    // Optimistic until the first load completes — matches the NavGroup
    // behaviour where items stay visible while permissions are en route.
    if (!permissionsLoaded.value) return true;
    return permissions.value.has(key.trim().toLowerCase());
  }

  async function loadPermissions(): Promise<void> {
    try {
      const list = await authApi.getMyPermissions();
      const next = new Set<string>();
      for (const name of list) next.add(name.trim().toLowerCase());
      permissions.value = next;
      permissionsLoaded.value = true;
    } catch {
      // Fall open on failure — better to show items that 403 at the
      // view level than hide the whole sidebar and strand the user.
      permissions.value = new Set();
      permissionsLoaded.value = false;
    }
  }

  async function login(userName: string, password: string): Promise<boolean> {
    loading.value = true;
    error.value = null;
    try {
      // The legacy /request/userApi/login controller wraps responses in
      // an envelope. Possible shapes after the axios interceptor unwraps
      // `data`:
      //   {authenticated: true}     — credentials accepted
      //   {authenticated: false}    — credentials rejected
      //   "" / false                — user not found (controller returns
      //                               PHP false; envelope coerces to "")
      //   throws ApiError           — envelope reported success:false
      let loginResult: { authenticated?: boolean } | null = null;
      try {
        const r = await authApi.login(userName, password);
        loginResult = r && typeof r === 'object' ? r : null;
      } catch (e: any) {
        // Could be bad creds, or could be a re-submit on an already-
        // logged-in tab. Check existing session before showing an error.
        if (await acceptExistingLoginSession()) return true;
        error.value = e?.message || 'Login failed';
        return false;
      }

      if (loginResult === null) {
        if (await acceptExistingLoginSession()) return true;
        error.value = 'Invalid username or password';
        return false;
      }

      if (!loginResult.authenticated) {
        error.value = 'Invalid username or password';
        return false;
      }

      // Backend says authenticated. Confirm by fetching user info —
      // also catches the failure mode where the session cookie didn't
      // round-trip (cookies blocked, cross-domain proxy, etc.). Without
      // this guard, getUserInfo's Guest fallback ({fName: "Guest"}) would
      // be assigned to user.value, isAuthenticated would be true, and
      // the app would behave as a logged-in user with no userId.
      const info = await authApi.getUserInfo();
      if (!info?.userId) {
        error.value =
          'Signed in, but the session did not persist. ' +
          'Check that cookies are enabled and try again.';
        return false;
      }
      user.value = info;
      loadPermissions();
      return true;
    } finally {
      loading.value = false;
    }
  }

  async function logout(): Promise<void> {
    try {
      await authApi.logout();
    } catch {
      // Server-side logout errors shouldn't prevent client-side cleanup
    }
    reset();
  }

  /**
   * Check whether there's already a valid session (cookie set) and populate
   * the user info. Called on app startup and after 401s clear the state.
   */
  async function checkSession(): Promise<boolean> {
    try {
      const info = await authApi.getUserInfo();
      if (info && info.userId) {
        user.value = info;
        loadPermissions();
        return true;
      }
      user.value = null;
      return false;
    } catch {
      user.value = null;
      return false;
    }
  }

  function reset(): void {
    user.value = null;
    error.value = null;
    permissions.value = new Set();
    permissionsLoaded.value = false;
  }

  return {
    user,
    loading,
    error,
    isAuthenticated,
    permissionsLoaded,
    login,
    logout,
    checkSession,
    reset,
    hasPermission,
  };
});
